<template>
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button class="button_back" @click="goBack">
        <ion-icon name="arrow-back-outline"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title v-if="job.startDate">
      <template v-if="checkJobDateStart(job.startDate, job.endDate)">
        <span>{{ checkJobDateStart(job.startDate, job.endDate, true) }}</span> -
      </template>
      <span v-if="isUS">{{
          $moment(job.endDate, "YYYY/MM/DD").format("MMM DD YYYY")
          }}</span>
      <span v-else>{{
          $moment(job.endDate, "YYYY/MM/DD").format("DD MMMM YYYY")
          }}</span>
    </ion-title>
    <ion-buttons slot="end">
      <ion-button class="button_more" @click="bottomDropUp = true" data-testid="button-more">
        <ion-icon name="ellipsis-vertical-outline"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>

  <div class="main_wrapper ion-padding">
    <JobDaysSkeleton v-if="pageLoading" class="container" :count="3" :is-us="isUS" />
    <div class="container" v-else>
      <div class="job_info_wrapper" v-if="job.pharmacy">
        <div class="image_text_comp d-block" @click="userInfoModalOpen()">
          <div class="image_wrapper">
            <el-avatar :size="'medium'" shape="square" :src="job.pharmacy.image" v-if="job.pharmacy.image" />
            <el-avatar :size="'medium'" shape="square" icon="el-icon-user-solid" v-else></el-avatar>
          </div>
          <div class="text_wrapper mt-2">
            <div class="main_text">{{ job.pharmacy.name }}</div>
            <div class="additional_text">
              <span class="distance">
                <span v-if="job.distance">{{ distanceInMiles(job.distance,'km',true) }} away</span>
                <span class="symb_infinity" v-else></span>
              </span>
            </div>
          </div>
        </div>

        <div class="info-separator" />
        
        <div class="rate_shifts_info">
          <div class="info_wrapper d-flex align-items-center">
            <ClockIcon />
            <div class="info_title">Available Shifts 
              <template v-if="checkJobDateStart(job.startDate, job.endDate)">
                <span>{{ checkJobDateStart(job.startDate, job.endDate, true) }}</span> -
              </template>
              <span v-if="isUS">{{
                  $moment(job.endDate, "YYYY/MM/DD").format("MMM DD YYYY")
                  }}</span>
              <span v-else>{{
                  $moment(job.endDate, "YYYY/MM/DD").format("DD MMMM YYYY")
                  }}</span>
            </div>
          </div>
          <div class="info_wrapper d-flex align-items-center">
            <LocationIcon />
            <div class="info_title">{{ $filters.addressText(job.pharmacy.address) }}</div>
          </div>
          <div class="info_wrapper d-flex align-items-center" v-if="showJobRateSummary">
            <DollarWithCircleIcon />
            <div class="info_title">Hourly Rate <span v-if="isAustralia">(excluding super)</span></div>
            <div class="info_value">
              {{
                formatHourlyRate(job.recommendedRate, job.recommendedWeekendRate)
              }}
            </div>
          </div>
        
        </div>

        <div class="clr-danger" v-if="!userData.locumVerifiedCvExist && job.applyJob && userData.role !== 'agency'">
          Please upload your CV to apply for this job
        </div>
        <div class="info-separator-without-border mb-0" />
        <el-collapse accordion class="job-about-collapse" @change="handleChange">
          <el-collapse-item class="card-accordeon" title="More about the job" name="first">
            <div class="collapse-list more-info">
              <div class="nested-collapse-wrapper" v-if="shouldDisplay">
                <el-collapse accordion="true" v-model="activeNames">
                  <el-collapse-item class="card-accordeon card-accordeon--nested" title="Script Numbers, Software and Services" name="1">
                    <div class="collapse-list more-info">
                      <div class="collapse-item" v-if="job.pharmacy.environment.scriptsPerDay">
                        <span class="collapse-title">Average scripts per day (weekdays)</span>
                        <span class="collapse-value">
                          {{ getScriptName(job.pharmacy.environment.scriptsPerDay) }}
                        </span>
                      </div>
                      <div class="collapse-item" v-if="job.pharmacy.averageScriptsSaturdayId">
                        <span class="collapse-title">Average scripts on Saturday</span>
                        <span class="collapse-value">
                          {{ getScriptName(job.pharmacy.averageScriptsSaturdayId) }}
                        </span>
                      </div>
                      <div class="collapse-item" v-if="job.pharmacy.averageScriptsSundayId">
                        <span class="collapse-title">Average scripts on Sunday</span>
                        <span class="collapse-value">
                          {{ getScriptName(job.pharmacy.averageScriptsSundayId) }}
                        </span>
                      </div>
                      <div class="collapse-item" v-if="job.pharmacy.environment.software">
                        <span class="collapse-title">Dispensing software</span>
                        <span class="collapse-value" v-if="getSoftwareName(job.pharmacy.environment.software) === 'Other'">
                          {{ getSoftwareName(job.pharmacy.environment.software) }}:
                          {{ job.pharmacy.settings.softwareOther }}
                        </span>
                        <span class="collapse-value" v-else>
                          {{ getSoftwareName(job.pharmacy.environment.software) }}
                        </span>
                      </div>
                      <div class="collapse-item collapse-item--services" v-if="job.pharmacy.environment.services">
                        <span class="collapse-title">Services</span>
                        <span class="collapse-value">
                          <el-tag class="white" v-for="service in job.pharmacy.environment.services" :key="service">
                            {{ service.name }}
                          </el-tag>
                        </span>
                      </div>
                    </div>
                  </el-collapse-item>
                </el-collapse>
              </div>
              <div class="collapse-item" v-if="showJobRateSummary">
                <span class="collapse-title">Hourly Rate <span v-if="isAustralia">(excluding super)</span></span>
                <span class="collapse-value">
                  {{
                    formatHourlyRate(
                      job.recommendedRate,
                      job.recommendedWeekendRate
                    )
                  }}
                </span>
              </div>

              <div v-if="job.settings.languages" class="collapse-item">
                <span class="collapse-title">Language</span>
                <span class="collapse-value">
                  {{ job.settings.languageNames }}
                </span>
              </div>

              <div class="collapse-item">
                <span class="collapse-title">Solo Pharmacist</span>
                <span class="collapse-value">{{
                  job.settings.solo == 1 ? "Yes" : "No"
                }}</span>
              </div>

              <div class="collapse-item">
                <span class="collapse-title">{{ message.Immuniser }} req?</span>
                <span class="collapse-value">{{
                  job.covidCertRequired == 0
                  ? "Not Required"
                  : job.covidCertRequired == 1
                    ? "Mandatory"
                    : "Nice to Have"
                }}</span>
              </div>

              <div class="collapse-item">
                <span class="collapse-title">Number of technicians</span>
                <span class="collapse-value">{{ job.settings.numOfTech }}</span>
              </div>
              <template v-if="!job.isInternalStaffOfThisPharmacy && job.isVisibileAsInternalLocum === false">
                <div class="collapse-item" v-if="job.settings.payingForTravel == 3 && job.maxTravelCompensation !== 0">
                  <span class="collapse-title">Pay for Travel</span>
                  <span class="collapse-value">{{ job.enableTravelAllowanceForFirstAndLastDay === 1 ? "Pay for first and last day of shift" : "Capped Travel" }}</span>
                </div>
                <div class="collapse-item" v-else>
                  <span class="collapse-title">Pay for Travel {{
                    job.settings.payingForTravel == 1 ? " (>50KM roundtrip)" : ""
                  }}</span>
                  <span class="collapse-value">{{
                      job.enableTravelAllowanceForFirstAndLastDay === 1
                        ? "Pay for first and last day of shift"
                        : job.settings.payingForTravel === 0
                          ? "No travel"
                          : job.settings.payingForTravel === 1
                            ? "Partial travel"
                            : "Full travel"
                    }}</span>
                </div>
              </template>

              <div class="collapse-item">
                <span class="collapse-title">Pay for Flights</span>
                <span class="collapse-value">{{
                  job.settings.payForFlights == 0 ? "No" : "Yes"
                }}</span>
              </div>
              <div class="collapse-item">
                <span class="collapse-title">Pay for Accommodation</span>
                <span class="collapse-value">{{
                  job.settings.payForAccommodation == 0 ? "No" : "Yes"
                }}</span>
              </div>
              <div class="collapse-item" v-if="isAustralia">
                <span class="collapse-title">Dosage Administration Aids</span>
                <span class="collapse-value">{{
                  job.settings.adminAids == 1 ? "Yes" : "No"
                }}</span>
              </div>
              <div class="collapse-item">
                <span class="collapse-title">Open Pharmacy</span>
                <span class="collapse-value">{{
                  job.settings.openUp == 1 ? "Yes" : "No"
                }}</span>
              </div>
              <div class="collapse-item">
                <span class="collapse-title">Close Pharmacy</span>
                <span class="collapse-value">{{
                  job.settings.close == 1 ? "Yes" : "No"
                }}</span>
              </div>
              <div v-if="job.settings.notes" class="collapse-item collapse-item--notes">
                <span class="collapse-title">Notes</span>
                <p class="job-notes__content">{{ job.settings.notes }}</p>
              </div>
              <div v-if="job.settings.parking" class="collapse-item collapse-item--notes">
                <span class="collapse-title">Parking</span>
                <p class="job-notes__content">{{ job.settings.parking }}</p>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
        <div class="info-separator-without-border mt-0" />
      </div>
      <div></div>
      <JobDaysSkeleton v-if="isLoadingShifts" :count="3" :is-us="isUS" shifts-only />
      <el-form v-else class="job_shifts_wrapper full_page" :model="formsData.dayForm" :rules="formsData.dayForm.rules"
        :ref="(el) => (formsData.dayForm.ref = el)" :label-position="'top'" @submit.prevent="submit('dayForm')">
        <div class="d-flex justify-content-between mb-4 align-items-center" >
          <div class="text-locumate-secondary fs-18 font-weight-600">Shifts</div>
          <el-button type="primary" round size="sm" @click="SelectAllShifts" v-if="formsData.dayForm.jobShifts.value.length > 1">
            {{ buttonLabel }}
          </el-button>
        </div>
        <!-- Display message if no shifts are available -->
        <div v-if="formsData.dayForm.jobShifts.value.length === 0" class="no-shifts-message">
          This shift has already been accepted by another locum. Check out other available shifts!
          <div class="d-flex justify-center size-button-home">
            <el-button class="el-button " type="blue" @click="goBack">
                Home
              </el-button>
          </div>
          
        </div>
        <template v-else>
          <div v-if="job.isBlockShift == 1 && formsData.dayForm.jobShifts.value.length >= 2">
            <div class="form_text">
              <div class="text_note">Important: This is a bulk shift. The pharmacy requests a single locum to book all listed shifts. Individual shift bookings are not available.</div>
            </div>
          </div>
          <div v-else-if="job.isBlockShift == 1 && formsData.dayForm.jobShifts.value.length <= 1">
          </div>
          <div class="job_shifts_form_checkbox" v-for="(shift, i) in formsData.dayForm.jobShifts.value" :key="shift.date">
            <el-form-item class="mb0" prop="jobShifts" v-if="isShiftAvailableToBook(shift)">
              <el-checkbox :disabled="shift.disabled" class="checkbox_w100" v-model="shift.checkboxValue"
                @change="checkboxValueChange(shift.checkboxValue)">
                <div :class="shift.confirmedHours ? 'confirmedHours' : ''" class="job_shift_text book-shift-card">
                  <div class="book-shift-card__date" v-if="isAustralia">
                    {{ $moment(shift.date, "YYYY/MM/DD").format("ddd") }},
                    {{ $moment(shift.date, "YYYY/MM/DD").format("DD MMM, YYYY") }}
                  </div>
                  <div class="book-shift-card__date" v-else>
                    {{ $moment(shift.date, "YYYY/MM/DD").format("dddd") }},
                    {{ $moment(shift.date, "YYYY/MM/DD").format("MMM DD YYYY") }}
                  </div>
                  <div class="info-wrapper">
                    <div class="day_info_block">
                      <ClockIcon />
                      <div class="day_time">
                        <span>{{
                          $moment(shift.startTime, "HH:mm").format("h:mm A")
                        }}</span>
                        -
                        <span>{{
                          $moment(shift.endTime, "HH:mm").format("h:mm A")
                        }}</span>
                        <span v-if="showShiftInformation(shift)">
                            ({{
                              confirmedHours(
                                shift.confirmedHours,
                                shift.startTime,
                                shift.endTime
                              )
                            }} Hours)</span>
                      </div>
                    </div>
                    <div class="day_info_block"
                      v-if="showShiftRateBlock(shift)">
                      <DollarWithCircleIcon />
                      <div class="day_name">Hourly rate:</div>
                      <div class="day_time">
                        <span v-if="showShiftInformation(shift)">
                          {{ Rate(shift) }}</span>
                        <span v-else>-</span>
                      </div>
                    </div>
                    <div class="day_info_block"
                      v-if="!job.isInternalStaffOfThisPharmacy && shift.isVisibileAsInternalLocum === false">
                      <TravelIcon color="rgb(var(--locumate-primary-color))" />
                      <div class="day_name" v-if="job.settings.payingForTravel == 3 && job.maxTravelCompensation !== 0">
                        <span>{{ job.enableTravelAllowanceForFirstAndLastDay === 1 ? "Travel" : "Capped Travel" }}:</span>
                      </div>
                      <div class="day_name" v-else>
                        <span>Travel:</span>
                      </div>
                      <div class="day_time">
                        <span v-if="showShiftInformation(shift)">
                          <span v-if="userData.isInternalStaffOfThisPharmacy">-</span>
                          <span v-else>{{ travelCompensationCount(job, shift, i) }}</span>
                        </span>
                        <span v-else>-</span>
                      </div>
                    </div>
                    <template v-if="isAustralia">
                      <div class="day_info_block"
                      v-if="showShiftRateBlock(shift)">
                      <DollarWithCircleIcon />
                        <div class="day_name">Total Super</div>
                          <div class="day_time">
                            <span v-if="showShiftInformation(shift)">
                              {{ superRate(shift) }}
                            </span>
                            <span v-else>-</span>
                          </div>
                      </div>
                    </template>
                    <div class="day_info_block"
                      v-if="showShiftRateBlock(shift)">
                      <DollarWithCircleIcon />
                      <div class="day_name">Total shift sum:</div>
                      <div class="day_time">
                        <span v-if="showShiftInformation(shift)">
                          {{ Sum(job, shift , i) }}</span>
                        <span v-else>-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </el-checkbox>
              <template #error="{ error }">
                <div class="error_list" v-if="error ||
                  (formsData.dayForm.jobShifts.errors &&
                    formsData.dayForm.jobShifts.errors[i])
                  ">
                  <div class="el-form-item__error" :class="formsData.dayForm.jobShifts.errors &&
                      formsData.dayForm.jobShifts.errors[i]
                      ? ''
                      : 'form_error'
                    ">
                    <span v-if="error && job.isBlockShift !== 1 &&
                      !formsData.dayForm.jobShifts.errors &&
                      formsData.dayForm.jobShifts.value.length - 1 == i
                      ">{{ error }}</span>
                    <span v-if="error && job.isBlockShift == 1 &&
                      !formsData.dayForm.jobShifts.errors &&
                      formsData.dayForm.jobShifts.value.length - 1 == i
                      ">Please select all days</span>
                    <span v-if="formsData.dayForm.jobShifts.errors &&
                      formsData.dayForm.jobShifts.errors[i]
                      ">{{ formsData.dayForm.jobShifts.errors[i].message }}</span>
                  </div>
                </div>
                <span v-else></span>
              </template>
            </el-form-item>
          </div>
          <div v-if="userData.role !== 'pharmacy'">
            <el-form-item prop="jobShiftsErrors" class="button_submit mb0" v-if="job.shiftApplicationType != 0 ">
              <el-button class="submit_page" type="grey" @click="goNext('dayForm')">
                Calendar View
              </el-button>
              <template v-if="userData.role === 'agency'">
                <el-button class="submit_page" type="blue" @click="bottomDropUpAcceptShift = true" :disabled="formsData.dayForm.disabled">
                  Mark as Filled
                </el-button>
              </template>
              <template v-else>
                <el-button class="submit_page" type="blue" @click="openAcceptShiftDrawer"
                  :disabled="formsData.dayForm.disabled || isButtonDisabled || !userData.locumVerifiedCvExist"
                  v-if="job.applyJob">
                  Apply now
                </el-button>
                <el-button class="submit_page" type="blue" @click="openAcceptShiftDrawer"
                  :disabled="formsData.dayForm.disabled || isButtonDisabled" v-else>
                  Accept now
                </el-button>
              </template>
            </el-form-item>
            <el-form-item prop="jobShiftsErrors" class="button_submit mb0" v-else>
              <el-button class="submit_page" type="grey" @click="goNext('dayForm')">
                Calendar View
              </el-button>
              <template v-if="userData.role === 'agency'">
                <el-button class="submit_page" type="blue" @click="bottomDropUpAcceptShift = true" :disabled="formsData.dayForm.disabled">
                  Mark as Filled
                </el-button>
              </template>
              <template v-else>
                <el-button class="submit_page" type="blue" @click="openAcceptShiftDrawer"
                  :disabled="formsData.dayForm.disabled || isButtonDisabled || !userData.locumVerifiedCvExist"
                  v-if="job.applyJob">
                  Apply now
                </el-button>
                <el-button class="submit_page" type="blue" @click="openAcceptShiftDrawer"
                  :disabled="formsData.dayForm.disabled || isButtonDisabled" v-else>
                  Accept now
                </el-button>
              </template>
            </el-form-item>
          </div>
        </template>
      </el-form>
    </div>
  </div>

  <el-drawer custom-class="bottom_drop_up accept_shift" v-model="bottomDropUpAcceptShift" :append-to-body="true" :with-header="false"
  :direction="'btt'" :destroy-on-close="true" data-testid="more-bottom-drop-up-drawer">
  <div class="title-wrapper text-center">
    <div class="title text-locumate-secondary">Are you sure!</div>
    <p class="message">
      {{ job.applyJob ? 'You will be notified of the outcome of your application once it has been reviewed. In the meantime, you can continue to apply for shifts on the same date.' : 'Please ensure you have checked your availability for selected shift(s). Any cancellations will impact your reliability rating on the platform.' }}
    </p>
  </div>
    
    <div class="job_shifts_form_checkbox" v-for="(shift,i) in formsData.dayForm.jobShifts.value" :key="shift.date">
      <div class="job_shifts_wrapper full_page" v-if="shift.checkboxValue == true && shift.abilities.assign == true">
        <div class="mb0 shift-check" prop="jobShifts">
          <div :class="shift.confirmedHours ? 'confirmedHours' : ''" class="job_shift_text book-shift-card">
            <div class="check-icon"><i class="el-icon-check"></i></div>
            <div class="book-shift-card__date shift_date" v-if="isAustralia">
              {{ $moment(shift.date, "YYYY/MM/DD").format("ddd") }},
              {{
                $moment(shift.date, "YYYY/MM/DD").format("DD MMM, YYYY")
              }}
            </div>
            <div class="book-shift-card__date shift_date" v-else>
              {{ $moment(shift.date, "YYYY/MM/DD").format("dddd") }},
              {{
                $moment(shift.date, "YYYY/MM/DD").format("MMM DD YYYY")
              }}
            </div>
            <div class="info-wrapper">
              <div class="day_info_block">
                <ClockIcon />
                <div class="day_time">
                  <span>{{
                    $moment(shift.startTime, "HH:mm").format("h:mm A")
                  }}</span>
                  -
                  <span>{{
                    $moment(shift.endTime, "HH:mm").format("h:mm A")
                  }}</span>
                  <span v-if="showShiftInformation(shift)">
                      ({{
                        confirmedHours(
                          shift.confirmedHours,
                          shift.startTime,
                          shift.endTime
                        )
                      }} Hours)
                      </span>
                </div>
              </div>
              <div class="day_info_block"
                v-if="showShiftRateBlock(shift)">
                <DollarWithCircleIcon />
                <div class="day_name">Hourly rate:</div>
                <div class="day_time">
                  <span v-if="showShiftInformation(shift)">
                    {{ Rate(shift) }}</span>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="day_info_block"
                v-if="!job.isInternalStaffOfThisPharmacy && shift.isVisibileAsInternalLocum === false">
                <TravelIcon color="rgb(var(--locumate-primary-color))" />
                <div class="day_name" v-if="job.settings.payingForTravel == 3 && job.maxTravelCompensation !== 0">
                  <span>{{ job.enableTravelAllowanceForFirstAndLastDay === 1 ? "Travel" : "Capped Travel" }}:</span>
                </div>
                <div class="day_name" v-else>
                  <span>Travel:</span>
                </div>
                <div class="day_time">
                  <span v-if="showShiftInformation(shift)">
                    <span v-if="userData.isInternalStaffOfThisPharmacy">-</span>
                    <span v-else>{{ travelCompensationCount(job, shift, i) }}</span>
                  </span>
                  <span v-else>-</span>
                </div>
              </div>
              <template v-if="isAustralia">
                <div class="day_info_block"
                    v-if="showShiftRateBlock(shift)">
                    <DollarWithCircleIcon />
                    <div class="day_name">Total Super:</div>
                    <div class="day_time">
                      <span v-if="showShiftInformation(shift)">
                        {{ superRate(shift) }}
                      </span>
                      <span v-else>-</span>
                    </div>
                  </div>
              </template>
              <div class="day_info_block"
                v-if="showShiftRateBlock(shift)">
                <DollarWithCircleIcon />
                <div class="day_name">Total shift sum:</div>
                <div class="day_time">
                  <span v-if="showShiftInformation(shift)">
                    {{ Sum(job, shift , i) }}</span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="dialog-footer" v-if="job.shiftApplicationType != 0">
      <el-button class="submit_page w-100 mb-3" type="blue" @click="applyShift('dayForm', job.id)"
      :disabled="formsData.dayForm.disabled || isButtonDisabled" v-if="job.applyJob">
        Apply now
      </el-button>
      <el-button class="submit_page w-100 mb-3" type="blue" @click="submit('dayForm')"
      :disabled="formsData.dayForm.disabled || isButtonDisabled" v-else>
        Accept now
      </el-button>
      <el-button class="w-100 ml-0" @click="bottomDropUpAcceptShift = false" type="grey">Cancel</el-button>
    </div>
    <div class="dialog-footer" v-else>
      <el-button class="submit_page w-100 mb-3" type="blue" @click="applyShift('dayForm', job.id)"
      :disabled="formsData.dayForm.disabled || isButtonDisabled" v-if="job.applyJob">
        Apply now
      </el-button>
      <el-button class="submit_page w-100 mb-3" type="blue" @click="submit('dayForm')"
      :disabled="formsData.dayForm.disabled || isButtonDisabled" v-else>
        Accept now
      </el-button>
      <el-button class="w-100 ml-0" @click="bottomDropUpAcceptShift = false" type="grey">Cancel</el-button>
    </div>
  </el-drawer>

  <el-drawer custom-class="bottom_drop_up menu-drawer" v-model="bottomDropUp" :append-to-body="true" :with-header="false"
    :direction="'btt'" :destroy-on-close="true" data-testid="more-bottom-drop-up-drawer">
    <!-- <el-buttons> -->
      <el-button type="transparent" class="d-block menu_item" @click="share(user)" data-testid="share-pharmacy-btn">
        <i class="el-icon-share"></i> Share
      </el-button>
      <el-button type="transparent" class="ml-0 d-block menu_item" @click="blockPharmacy()"
        data-testid="block-unblock-pharmacy-btn">
        <template v-if="job.blockedByLocum">
          <i class="el-icon-unlock mr-1 font-weight-bold"></i> Unblock Pharmacy
        </template>
        <template v-else>
          <i class="locumate_icons locumate_icon-denied mr-1"></i> Block Pharmacy
        </template>
      </el-button>
    <!-- </el-buttons> -->
  </el-drawer>
  <modal-user-info :modalName="'userInfoModal'" :options="userInfoModal"></modal-user-info>
  <MissingProfileRequirementsModal
    v-model="modalVisible"
    :missing="modalMissing"
    :is-apply-job="job.applyJob"
    @complete="goToRequirement"
  />
</template>

<script>
import { ElMessage, ElMessageBox } from "@/components/Vendor/Element";

import moment from "moment";

import { ref, onMounted, onBeforeMount, inject, nextTick, computed } from "vue";

import { addIcons } from "ionicons";
import { arrowBackOutline, ellipsisVerticalOutline } from "ionicons/icons";

addIcons({
  "arrow-back-outline": arrowBackOutline,
  "ellipsis-vertical-outline": ellipsisVerticalOutline
});
import {
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonTitle,
} from "@ionic/vue";

import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import formHooks from "@/hooks/form/form";
import formValidate from "@/hooks/form/validate/validate";

import { UserInfoModal } from "@/modals/modals";
import timeDiff from "@/helpers/date/timeDiff";
import timeDiffAsDecimal from "@/helpers/date/timeDiffAsDecimal";
import { locumCanSeeShiftRates, locumCanSeeJobRates } from "@/helpers/utility/locumShiftRates";
import store from "@/store/store";
import ClockIcon from "@/components/Icons/ClockIcon.vue";
import LocationIcon from "@/components/Icons/LocationIcon.vue";
import DollarWithCircleIcon from "@/components/Icons/DollarWithCircleIcon.vue";
import TravelIcon from "@/components/Icons/TravelIcon.vue";
import { isAustraliaRegion,isUSRegion } from '@/helpers/region/regionHelper.js';
import distanceInMiles from "@/helpers/distance/distance-in-miles.js";
import MissingProfileRequirementsModal from "@/modals/Profile/MissingProfileRequirementsModal.vue";
import { useProfileRequirements } from "@/composables/useProfileRequirements.js";
import JobDaysSkeleton from "@/views/Job/Shifts/Book/Days/JobDaysSkeleton.vue";
import { getEssentialJobData } from "@/helpers/utility/jobDataSelector.js";

export default {
  components: {
    IonToolbar,
    IonButton,
    IonButtons,
    IonIcon,
    IonTitle,
    "modal-user-info": UserInfoModal,
    MissingProfileRequirementsModal,
    ClockIcon,
    LocationIcon,
    DollarWithCircleIcon,
    TravelIcon,
    JobDaysSkeleton,
  },
  directives: {},
  setup() {
    const $store = useStore();
    const $router = useRouter();
    const route = useRoute();
    const dialogVisible = ref(false);
    const currentUrl = process.env.VUE_APP_CURRENT_URL;
    const setPageClassGlobal = inject("setPageClassGlobal");
    const $modals = inject("$modals");
    const message = inject('messages');
    const googleMaps = inject(["googleMaps"]);
    const bottomDropUp = ref(false);
    const bottomDropUpAcceptShift = ref(false);
    const profileRequirements = useProfileRequirements();

    // Bulk shifts are booked together, so any shift in the requirement window applies.
    // Otherwise only the shifts the locum actually selected are considered.
    function selectionRequiresProfileRequirements() {
      const shifts = formsData.dayForm.jobShifts.value || [];
      const relevantShifts = job.value?.isBlockShift == 1
        ? shifts
        : shifts.filter((shift) => shift.checkboxValue === true);

      return relevantShifts.some((shift) =>
        profileRequirements.shiftRequiresProfileRequirements(shift.date)
      );
    }

    function openAcceptShiftDrawer() {
      if (!selectionRequiresProfileRequirements()) {
        bottomDropUpAcceptShift.value = true;
        return;
      }

      profileRequirements.guardJobBooking(() => {
        bottomDropUpAcceptShift.value = true;
      }, {
        pharmacyId: job.value?.pharmacyId,
        jobProfileRequirements: job.value?.profileRequirements,
        isContractorForPharmacy: job.value?.isContractorForPharmacy,
        contractorInvoicingEnabled: job.value?.contractorInvoicingEnabled,
        internalStaffForPharmacy: job.value?.isInternalStaffOfThisPharmacy
          || job.value?.profileRequirements?.internalStaffForPharmacy,
      });
    }
    const setting = ref({});
    const activeNames = ref(["1"]);
    const isAustralia = isAustraliaRegion();
    const isUS = isUSRegion();
    let job = ref({});
    let shifts = ref([]);
    const pageLoading = ref(true);
    const isLoadingShifts = ref(false);
    const $moment = inject(["$moment"]);
    const userData = $store.getters.GET_USER_DATA;
    const groupSubscriptionExist = ref(userData.groupSubscriptionExist ? userData.groupSubscriptionExist : false);
    const userRole = userData.role;

    const showJobRateSummary = computed(() => {
      const currentJob = job.value;
      if (
        !currentJob ||
        currentJob.isInternalStaffOfThisPharmacy ||
        currentJob.isVisibileAsInternalLocum !== false
      ) {
        return false;
      }
      if (userRole !== "locum") {
        return true;
      }
      if (currentJob.hideRecommendedRateForLocum === true) {
        return false;
      }
      return locumCanSeeJobRates(currentJob.shifts, userRole);
    });

    const showShiftRateBlock = (shift) => {
      if (!job.value || job.value.isInternalStaffOfThisPharmacy || shift?.isVisibileAsInternalLocum !== false) {
        return false;
      }
      return locumCanSeeShiftRates(shift, userRole);
    };
    const isButtonDisabled = computed(() => {
      // return job.value.isInternalStaffOfThisPharmacy && !groupSubscriptionExist.value;
      return false;
    });

    const formsDataArray = {
      dayForm: {
        jobShifts: {
          value: [],
          errors: null,
          validators: [
            {
              validator: (rule, formField, callback) => {
                let errors = formsData.dayForm.jobShifts.errors;
                let formName = formField.formName;

                if (!errors || !errors.length) {
                  setFormValidation(formName, rule.field, true);
                  callback();
                } else {
                  setFormValidation(formName, rule.field, false);
                  callback(new Error(""));
                }
              },
            },
            {
              validator: (rule, formField, callback) => {
                formValidate.validate(
                  rule,
                  formField,
                  "jobShiftsBook",
                  callback,
                  setFormValidation
                );
              },
            },
          ],
        },
      },
    };

    (async () => {
      try {
        if (!job.value.distance) {
          let needToRetrieveDistance = true;
          let jobsDistancesCache = store.getters.getJobDistancesData.length
            ? store.getters.getJobDistancesData
            : []; // data from cache

          for (let cacheItem of jobsDistancesCache) {
            if (cacheItem.id == job.value.pharmacyId && cacheItem.cachedAt) {
              //check if distance exists in cache
              console.log("distance item loaded from cache");
              console.log(cacheItem.distance);

              needToRetrieveDistance = false;
              job.value.distance = cacheItem.distance;
              break;
            }
          }

          if (needToRetrieveDistance) {
            const result = await googleMaps.getDistance(userData.address, [
              job.value.pharmacy.address,
            ]);

            let distance = null;
            let jobDistance = result[0].elements[0].distance
              ? result[0].elements[0].distance.text
              : null;

            if (jobDistance) {
              jobDistance = jobDistance.replace(",", ".");
              if (new RegExp("(.*)(km|км)$", "gm").test(jobDistance)) {
                distance = parseFloat(jobDistance);
              } else if (new RegExp("(.*)(m|м)$", "gm").test(jobDistance)) {
                distance = parseFloat(jobDistance) / 1000;
              } else {
                console.log("");
              }
            }
            console.log("----- set distance -----");
            console.log(distance);

            job.value.distance = distance;
          }
        }
      } catch (error) {
        console.log(error);
      }
    })();

    // Computed property to determine the button label
    const buttonLabel = computed(() => {
      const allChecked = formsData.dayForm.jobShifts.value.every(
        (shift) => shift.disabled || shift.checkboxValue === true
      );
      return allChecked ? 'Unselect All Shifts' : 'Select All Shifts';
    });

    const SelectAllShifts = () => {
      // Determine if all enabled shifts are already checked
      const allChecked = formsData.dayForm.jobShifts.value.every(
        (shift) => shift.disabled || shift.checkboxValue === true
      );

      // Set the checkboxValue for each shift accordingly
      formsData.dayForm.jobShifts.value.forEach((shift) => {
        if (!shift.disabled) {
          shift.checkboxValue = !allChecked;
        }
      });
    };


    const checkboxValueChange = (value) => {
      if (job.value.isBlockShift == 1) {
        formsData.dayForm.jobShifts.value.forEach((shift) => {
          if (value && shift.abilities && shift.abilities["assign"] == true) {
            shift.checkboxValue = true;
          } else {
            shift.checkboxValue = false;
          }
        });
        nextTick(() => {
          checkFormValidation("dayForm", true);

        });
      } else {
        if (formsData.dayForm.jobShifts.errors) {
          delete formsData.dayForm.jobShifts.errors;
        }
        nextTick(() => {
          checkFormValidation("dayForm", true);
        });
      }
    };

  
    const checkJobDateStart = (dateStart, endDate, trim = true) => {
            let start = $moment(dateStart, "YYYY/MM/DD");
            let end = $moment(endDate, "YYYY/MM/DD");

            if (start.format("YYYY") !== end.format("YYYY")) {
                // If years are different, return full date with year
                if (isAustralia.value) {
                    return start.format("DD MMM YYYY");
                } else {
                    return start.format("MMM DD YYYY");
                }
             }

            if (start.format("MMMM") == end.format("MMMM")) {
                if (start.format("D") == end.format("D")) {
                    return null;
                }
                if(isAustralia.value){

                    return start.format("DD ");
                }else{
                    return start.format("MMM DD");
                }
            } else {
              if(isAustralia.value){
                    return start.format("DD " + (trim ? "MMM" : "MMMM") );
                }else{
                    return start.format( (trim ? "MMM " : "MMMM ") + "DD" );
                }
            }
        };

    const getSelectedShiftsId = () => {
      return formsData.dayForm.jobShifts.value
        .filter((day) => {
          if (day.checkboxValue == true) {
            return true;
          }
        })
        .map((day) => day.id);
    };

    const userInfoModal = ref({
      name: "userInfo",
      user: {},
    });

    const userInfoModalOpen = () => {
      userInfoModal.value.user = job.value.pharmacy;
      $modals.open("userInfoModal");
    };

    const goBack = () => {
      if(userRole==='locum' || userRole==='pharmacy' || userRole==='agency'){
        goToSearch();

      }else{
        $router.push({ name: "job_seeker_index" });
      }
      
    };

    const goToSearch = () => {
      $router.push({ name: "search" });
    }

    const goNext = () => {
      $store.dispatch("setJobDetails", {
        job: job.value,
      });
      $router.push({
        name: "job_book_calendar",
      });
    };


    if (job.value.blockedByLocum) {

      ElMessage({

        type: "error",

        message: "You have blocked this pharmacy. Please unblock to apply for this job.",

      });

      goToSearch();

    }
    
    //job shift apply function
    const applyShift = (formName, jobId) => {
      formsRefs[formName].value.validate((valid) => {
        if (valid) {
          (async () => {
            try {
              await $store.dispatch("APPLY_JOB_SHIFTS_LIST", {
                shift_ids: getSelectedShiftsId(),
                job_id: jobId
              });
              ElMessage({
                type: "success",
                message: "Thank you for applying for the Shift. We will notify you when the shift is offered to you",
              });
              $router.push({
                name: "search",
              });
            } catch (error) {
              let errorMessage = [];
              formsData.dayForm.jobShifts.value.filter((shift) => {
                let errorsArray = error.errors.some((element) => {
                  if (element.id == shift.id) {
                    errorMessage.push({
                      date: shift.date,
                      message: element.message,
                    });
                    return true;
                  }
                });
                if (!errorsArray) errorMessage.push(null);
              });
              console.log(errorMessage);
              formsData.dayForm.jobShifts.errors = errorMessage;
              checkFormValidation("dayForm", true);
            }
          })();
        } else {
          console.log("error submit!!");
          return false;
        }
      });
    };

    const submit = (formName) => {
      formsRefs[formName].value.validate((valid) => {
        console.log("checking validation", valid);
        if (valid) {
          (async () => {
            try {
              await $store.dispatch("SAVE_JOB_SHIFTS_LIST", {
                shifts_ids: getSelectedShiftsId(),
              });
              ElMessage({
                type: "success",
                message: "Thank You! Your booking has been confirmed.",
              });


              $router.push({
                name: "search",
              });
              if (!job.value.collaboratedBefore)
                $router.push({
                  name: "chat_message",
                  params: {
                    chatId: `${job.value.pharmacyId}.${userData.id}`,
                    firstCollaborate: true,
                  },
                });

            } catch (error) {
              let errorMessage = [];
              formsData.dayForm.jobShifts.value.filter((shift) => {
                let errorsArray = error.errors.some((element) => {
                  if (element.id == shift.id) {
                    errorMessage.push({
                      date: shift.date,
                      message: element.message,
                    });
                    return true;
                  }
                });
                if (!errorsArray) errorMessage.push(null);
              });
              console.log(errorMessage);
              formsData.dayForm.jobShifts.errors = errorMessage;
              checkFormValidation("dayForm", true);
            }
          })();
        } else {
          console.log("error submit!!");
          return false;
        }
      });
    };

    onBeforeMount(() => {
      pageLoading.value = true;
      $store
        .dispatch("GET_JOB_INFO", {
          jobId: route.params.jobId,
          params: {
            with: "pharmacy,pharmacy.settings,pharmacy.address,settings",
            navigation: "locum_job_book_days",
            hideLoading: true,
          },
        })
        .then(async (response) => {
          if (!response || !response.data) {
            goToSearch();
            return;
          }
          $store.dispatch("setJobDetails", getEssentialJobData(response.data));
          processJobData(response.data);
          await fetchShifts();
        })
        .catch(() => {
          goToSearch();
        })
        .finally(() => {
          pageLoading.value = false;
          nextTick(() => {
            setFormRefs();
            checkFormValidation("dayForm", true);
          });
        });
    });

    const processJobData = (jobFetchResult) => {
      if(jobFetchResult.error) {
        ElMessage({
          type: "error",
          message: jobFetchResult.message,
        });
        goToSearch();
        return;
      }

      job.value = jobFetchResult;
      
      if (jobFetchResult.blacklistedByPharmacy) {
        goToSearch();
        return;
      }
      
      if (jobFetchResult.blockedByLocum) {
        ElMessage({
          type: "error",
          message: "You have blocked this pharmacy. Please unblock to apply for this job.",
        });
        goToSearch();
        return;
      }
      
      job.value.blockedByLocum = jobFetchResult.blockedByLocum;
      job.value.distance = jobFetchResult.distance;
    };

    const attachJobContextToShift = (shift) => {
      if (!shift.job) {
        shift.job = {
          isInternalStaffOfThisPharmacy: job.value?.isInternalStaffOfThisPharmacy,
        };
      }
      if (shift.isVisibileAsInternalLocum === undefined) {
        shift.isVisibileAsInternalLocum = job.value?.isVisibileAsInternalLocum ?? false;
      }
      if (typeof shift.date === "string" && shift.date.includes("-") && !shift.date.includes("/")) {
        const parsed = moment(shift.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
        if (parsed.isValid()) {
          shift.date = parsed.format("YYYY/MM/DD");
        }
      }
    };

    const listedShiftsCount = (jobObj) => {
      if (Array.isArray(jobObj?.shifts) && jobObj.shifts.length > 0) {
        return jobObj.shifts.length;
      }
      return formsData.dayForm.jobShifts.value?.length || 0;
    };

    const isShiftAvailableToBook = (shift) => {
      const applications = Number(shift?.totalShiftApplications ?? 0);
      const limit = Number(job.value?.maximumApplicationLimit ?? 0);
      const underLimit = limit <= 0 || applications < limit;
      const canAssign = shift?.abilities?.assign == true;
      return underLimit && canAssign;
    };

    const processShifts = (shiftsArray) => {
      if (!Array.isArray(shiftsArray)) return [];

      shiftsArray.forEach((shift) => {
        attachJobContextToShift(shift);

        moment().diff(
          moment(
            shift.date + " " + shift.startTime,
            ["YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss", "DD MMM YYYY HH:mm:ss"]
          ),
          "minutes"
        ) <= 0
          ? (shift.disabled = false)
          : (() => {
            shift.disabled = true;
            shift.checkboxValue = false;
          })();

        if (shift.userId) {
          shift.disabled = true;
          shift.checkboxValue = false;
        }

        if (shift.abilities) {
          const isAssignAvailable = shift.abilities["assign"] == true;
          shift.checkboxValue = isAssignAvailable ? true : false;
          shift.disabled = isAssignAvailable ? false : true;
        }

        const applications = Number(shift.totalShiftApplications || shift.total_shift_applications || 0);
        const applicationLimit = Number(job.value.maximumApplicationLimit || job.value.maximum_application_limit || 0);
        if (applicationLimit > 0 && applications >= applicationLimit) {
          shift.disabled = true;
          shift.checkboxValue = false;
        }

        if (shift.shiftApplicationData && shift.shiftApplicationData.status) {
          shift.disabled = true;
          shift.checkboxValue = false;
        }
        if (shift.checkboxValue == undefined) {
          shift.checkboxValue = true;
        }
      });

      return shiftsArray.filter((object) => object.disabled !== true);
    };

    const fetchShifts = async () => {
      if (!job.value?.id || isLoadingShifts.value) {
        return;
      }

      isLoadingShifts.value = true;
      try {
        const perPage = 50;
        let page = 1;
        let lastPage = 1;
        let allShifts = [];

        do {
          const response = await $store.dispatch("GET_JOB_SHIFTS_LIST", {
            jobId: job.value.id,
            params: {
              page,
              per_page: perPage,
              hideLoading: true,
              navigation: "locum_job_book_days",
            },
          });

          let shiftsData = [];
          if (response?.data && Array.isArray(response.data)) {
            shiftsData = response.data;
          } else if (Array.isArray(response)) {
            shiftsData = response;
          }

          allShifts = allShifts.concat(shiftsData);

          const meta = response?.meta || {};
          lastPage = Number(meta.lastPage || meta.last_page || 0);
          if (!lastPage) {
            lastPage = shiftsData.length < perPage ? page : page + 1;
          }
          page += 1;
        } while (page <= lastPage && page <= 50);

        const processed = processShifts(allShifts);
        formsData.dayForm.jobShifts.value = processed;
        shifts.value = processed;
        formsData.dayForm.disabled = processed.length <= 0;
      } catch (error) {
        formsData.dayForm.jobShifts.value = [];
        formsData.dayForm.disabled = true;
      } finally {
        isLoadingShifts.value = false;
        nextTick(() => {
          setFormRefs();
          checkFormValidation("dayForm", true);
        });
      }
    };

    onMounted(() => {
      setPageClassGlobal("global_job_book_page");
      setFormRefs();
      checkFormValidation("dayForm", true);
    });

    const formatHourlyRate = (regularRate, weekendRate) => {
      let formattedString = "";
      if (regularRate) {

        formattedString += formatCurrency(regularRate);
      }

      if (regularRate && weekendRate) {
        formattedString += " - ";
      }

      if (weekendRate) {
        formattedString += formatCurrency(weekendRate);
      }
      return formattedString;
    };

    const formatCurrency = (rate) => {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(rate);
    };

    const handleChange = (e) => {
      e.stoppropagation();
    };
    const confirmedHours = (confirmedHours, startTime, endTime) => {
      let hours = 0;
      if (confirmedHours == 0 || confirmedHours == null) {
        const start = moment(startTime, "HH:mm:ss");
        const end = moment(endTime, "HH:mm:ss");

        hours = timeDiff(start, end);
      } else {
        const string = String(confirmedHours);
        const numHours = string.split(".")[0];
        let minutes = string.split(".")[1];

        if (!minutes) {
          const time = numHours + ":" + "00";
          hours = String(time);
        } else {
          minutes = Number.parseFloat(`0.${minutes}`) * 60;
          hours = numHours + ":" + minutes.toFixed(0);
        }
      }

      return hours;
    };
    const Sum = (job, shift, i) => {
      if (!locumCanSeeShiftRates(shift, userRole)) {
        return "-";
      }
      let Sum;
      let confirmedHoursVal;
      let payRate;
      let travelCompensation = 0;
      const globalTravelCompensationValue =
        process.env.VUE_APP_TRAVEL_COMPENSATION;

      if (userRole === "locum") {
        if (shift.locum == null && (shift.payRateExecSuper == null || shift.payRateExecSuper == 0)) {
          payRate = shift.recommendedRate;
        } else {
          payRate = shift.payRateExecSuper;
        }
      } else {
        payRate = shift.payRateExecSuper;
      }

      if (shift.confirmedHours == 0 || shift.confirmedHours == null) {
        const start = moment(shift.startTime, "HH:mm:ss");
        const end = moment(shift.endTime, "HH:mm:ss");

        confirmedHoursVal = timeDiffAsDecimal(start, end);
      } else {
        confirmedHoursVal = shift.confirmedHours;
      }

      if (shift.travelCompensation === 0 && shift.adminUpdatedTravelCompensation == false) {
        let jobDistance = shift.distance ? shift.distance : null;

        if (jobDistance === null && job.distance) {
          jobDistance = job.distance;
        }

        if (job.isInternalStaffOfThisPharmacy) {
          jobDistance = 0;
        }

        let distance = jobDistance;

        if (job.settings?.payingForTravel == 1) {
          if (distance * 2 > 50) {
            travelCompensation =
              (distance * 2 - 50) * globalTravelCompensationValue;
          } else {
            travelCompensation = 0;
          }
        } else if (job.settings?.payingForTravel == 2) {
          travelCompensation = distance * 2 * globalTravelCompensationValue;
        } else if (job.settings?.payingForTravel === 3 && job.maxTravelCompensation !== 0) {
          let realTravelCost = distance * 2 * globalTravelCompensationValue;
          if (realTravelCost > job.maxTravelCompensation) {
            travelCompensation = job.maxTravelCompensation;
          } else {
            travelCompensation = distance * 2 * globalTravelCompensationValue;
          }
        }
        if (!job.settings?.payingForTravel) {
          travelCompensation = 0;
        }
      } else {
        travelCompensation = shift.travelCompensation;
      }

      // 🚀 APPLY FIRST & LAST SHIFT LOGIC (skip when admin set per-shift travel)
      if (job.isBlockShift == 1 && job.enableTravelAllowanceForFirstAndLastDay === 1 && shift.adminUpdatedTravelCompensation !== true) {
        const shiftsLength = listedShiftsCount(job);
        const isFirstShift = i === 0;
        const isLastShift = i === shiftsLength - 1;

        if (shiftsLength > 1 && (isFirstShift || isLastShift)) {
          travelCompensation = travelCompensation / 2;
        } else if (shiftsLength > 1) {
          travelCompensation = 0;
        }
      }

      Sum = payRate * confirmedHoursVal + travelCompensation;
      if (Sum != 0) {
        Sum = new Intl.NumberFormat("en-AU", {
          style: "currency",
          currency: "AUD",
        }).format(Sum);
      } else {
        Sum = "-";
      }
      return Sum;
    };

    const Rate = (shift) => {
      if (!locumCanSeeShiftRates(shift, userRole)) {
        return "-";
      }
      if (userRole === "locum") {
        if (shift.locum == null && (shift.payRateExecSuper == null || shift.payRateExecSuper == 0)) {
          return Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(shift.recommendedRate);
        } else {
          return Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(shift.payRateExecSuper);
        }
      }
    };
    const superRate = (shift) => {
      if (!locumCanSeeShiftRates(shift, userRole)) {
        return "-";
      }
      if (userRole === "locum") {
        
         let confirmedHoursVal = 0;
        if (shift.confirmedHours == 0 || shift.confirmedHours == null) {
        const start = moment(shift.startTime, "HH:mm:ss");
        const end = moment(shift.endTime, "HH:mm:ss");

        confirmedHoursVal = timeDiffAsDecimal(start, end);
      } else {
        confirmedHoursVal = shift.confirmedHours;
      }
                

        const superRate = shift.superRate * confirmedHoursVal;
        return Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(superRate);
       
      }
    };

    const distance = (job, shift) => {
      let result;
      const globalTravelCompensationValue =
        process.env.VUE_APP_TRAVEL_COMPENSATION;

      if (!shift.distance && !job.distance) {
        result = "-";
      } else {
        let distance;
        distance = shift.historyDistance ? shift.historyDistance : job.distance;
        if (job.settings.payingForTravel == 1) {
          if (distance * 2 > 50) {
            const result1 = (distance * 2 - 50) * globalTravelCompensationValue;
            result = new Intl.NumberFormat("en-AU", {
              style: "currency",
              currency: "AUD",
            }).format(result1);
          } else {
            result = "-";
          }
        } else if (job.settings.payingForTravel == 2) {
          result = new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(distance * 2 * globalTravelCompensationValue);
        } else if (job.settings.payingForTravel == 3) {
          result = new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(job.maxTravelCompensation);
        }
      }

      return result;
    };
    const share = () => {
      bottomDropUp.value = false;
      const link = currentUrl + "job-preview/" + job.value.id;
      const el = document.createElement("textarea");
      el.value = link;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      ElMessageBox.alert("Link to current job copied to clipboard", "Thanks", {
        type: "success",
        showCancelButton: false,
        confirmButtonText: "Close",
        callback: () => {
          console.log("btn click");
        },
      });
    };

    const blockPharmacy = () => {
      bottomDropUp.value = false;
      const isPharmacyBlocked = job.value.blockedByLocum;
      const type = isPharmacyBlocked ? 'unblock' : 'block';
      let title = isPharmacyBlocked ? "Are you sure you want to unblock this pharmacy?" : "Are you sure you want to block this pharmacy?";
      let message = isPharmacyBlocked ? "" : "You will no longer see their shifts";
      ElMessageBox.confirm(
        message, title,
        {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }
      )
        .then(() => {
          $store
            .dispatch("BLOCK_PHARMACY", {
              pharmacyId: job.value.pharmacyId,
              type: type
            })
            .then(() => {
              ElMessage({
                type: "success",
                message: `Pharmacy ${type}ed successfully`,
              });
              $store.dispatch("GET_LOCUM_BLOCKED_PHARMACIES");
              goToSearch();
            });
        })
        .catch(() => { });
    }

    const showShiftInformation = (shift) => {
      const isInternalStaff =
        shift?.job?.isInternalStaffOfThisPharmacy ??
        job.value?.isInternalStaffOfThisPharmacy;
      if (isInternalStaff && shift.shiftSubscription) {
        return false;
      }
      if (!shift.userId) return true;
      if (shift.userId === userData.id) return true;
      return false;
    };

    // const travelCompensationCount = (job, shift, i) => {
    //   console.log("🚀 ~ travelCompensationCount ~ i:", i)
    //   if ((shift.travelCompensation >= 0 && shift.adminUpdatedTravelCompensation == true) || (shift.travelCompensation === 0 && shift.adminUpdatedTravelCompensation == true)) {
       
    //     const convertedTravelRate = formatCurrency(shift.travelCompensation);
    //     return convertedTravelRate;
    //   } else {
    //     if (
    //       job.settings.payingForTravel === 0 &&
    //       shift.adminUpdatedTravelCompensation == false
    //     ) {
    //       return "-";
    //     }

    //     if (job.settings.payingForTravel === 3 && job.maxTravelCompensation !== 0) {
    //       let travelCompensation;
          
    //       //admin may change the travel compensation for the job via job menu in the admin panel
    //       //the changed travel compensation only reflects on the shifts that has not been booked
    //       //so to keep track of previous travel compensations, we store the travel compensation in the shift table as well naming the column as job_max_travel_compensation
    //       //job_max_travel_compensation in the shift table is updated when admin updates the travel compensation for the job
    //       //where the shifts job_max_travel_compensation that has been booked is updated with previous travel compensation and the shifts that has not been booked is updated with the new travel compensation
    //       let maxTravelCompensation = shift.jobMaxTravelCompensation && shift.userId ? shift.jobMaxTravelCompensation : job.maxTravelCompensation;
    //       const globalTravelCompensationValue = process.env.VUE_APP_TRAVEL_COMPENSATION;
    //       let jobDistance;

    //       if (shift.distance) {
    //         jobDistance = shift.distance;
    //       } else {
    //         jobDistance = shift.historyDistance ? shift.historyDistance : job.distance;
    //       }

    //       if (jobDistance) {
    //         let distance = jobDistance;
    //         travelCompensation = distance * 2 * globalTravelCompensationValue;
    //         if (travelCompensation > maxTravelCompensation) {
    //           const convertedTravelRate = formatCurrency(maxTravelCompensation);
    //           return convertedTravelRate;
    //         } else {
    //           travelCompensation = new Intl.NumberFormat("en-AU", {
    //             style: "currency",
    //             currency: "AUD",
    //           }).format(distance * 2 * globalTravelCompensationValue);
    //           return travelCompensation;
    //         }
    //       } else {
    //         const convertedTravelRate = formatCurrency(maxTravelCompensation);
    //         return convertedTravelRate;
    //       }

    //     } else {
    //       return distance(job, shift);
    //     }

    //   }

    // };


    const travelCompensationCount = (job, shift, i) => {
      let travelCompensation = calculateTravelCompensation(job, shift); // Call the original function

      if (shift.adminUpdatedTravelCompensation === true) {
        return travelCompensation;
      }

      if (job.isBlockShift == 1 && job.enableTravelAllowanceForFirstAndLastDay === 1) {
        const shiftsLength = listedShiftsCount(job);
        if (shiftsLength < 2) {
          return travelCompensation;
        }
        const isFirstShift = i === 0;
        const isLastShift = i === shiftsLength - 1;

        if (isFirstShift || isLastShift) {
          if (typeof travelCompensation === "string" && travelCompensation === "-") {
            return "-"; // Keep '-' as it is
          }
          
          // Extract numeric value from currency format (handles cases like "$8.00")
          let numericValue = parseFloat(travelCompensation.replace(/[^0-9.-]+/g, ""));
          let halfCompensation = numericValue / 2;

          // Format back to currency using Intl.NumberFormat
          return new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
          }).format(halfCompensation);
        } else {
          return "-";
        }
      }

      return travelCompensation; // Default behavior
    };


    // Helper function to calculate the full travel compensation
    const calculateTravelCompensation = (job, shift) => {
      if ((shift.travelCompensation >= 0 && shift.adminUpdatedTravelCompensation == true) || (shift.travelCompensation === 0 && shift.adminUpdatedTravelCompensation == true)) {
       
        const convertedTravelRate = formatCurrency(shift.travelCompensation);
        return convertedTravelRate;
      }else if(shift.travelCompensation >= 0 ){
        const convertedTravelRate = formatCurrency(shift.travelCompensation);
        return convertedTravelRate;
      } else {
        if (
          job.settings?.payingForTravel === 0 &&
          shift.adminUpdatedTravelCompensation == false
        ) {
          return "-";
        }

        if (job.settings?.payingForTravel === 3 && job.maxTravelCompensation !== 0) {
          let travelCompensation;
          
          //admin may change the travel compensation for the job via job menu in the admin panel
          //the changed travel compensation only reflects on the shifts that has not been booked
          //so to keep track of previous travel compensations, we store the travel compensation in the shift table as well naming the column as job_max_travel_compensation
          //job_max_travel_compensation in the shift table is updated when admin updates the travel compensation for the job
          //where the shifts job_max_travel_compensation that has been booked is updated with previous travel compensation and the shifts that has not been booked is updated with the new travel compensation
          let maxTravelCompensation = shift.jobMaxTravelCompensation && shift.userId ? shift.jobMaxTravelCompensation : job.maxTravelCompensation;
          const globalTravelCompensationValue = process.env.VUE_APP_TRAVEL_COMPENSATION;
          let jobDistance;

          if (shift.distance) {
            jobDistance = shift.distance;
          } else {
            jobDistance = shift.historyDistance ? shift.historyDistance : job.distance;
          }

          if (jobDistance) {
            let distance = jobDistance;
            travelCompensation = distance * 2 * globalTravelCompensationValue;
            if (travelCompensation > maxTravelCompensation) {
              const convertedTravelRate = formatCurrency(maxTravelCompensation);
              return convertedTravelRate;
            } else {
              travelCompensation = new Intl.NumberFormat("en-AU", {
                style: "currency",
                currency: "AUD",
              }).format(distance * 2 * globalTravelCompensationValue);
              return travelCompensation;
            }
          } else {
            const convertedTravelRate = formatCurrency(maxTravelCompensation);
            return convertedTravelRate;
          }

        } else {
          return distance(job, shift);
        }

      }

    };




    const getScriptName = (scriptId) => {
      let allScript = setting.value.scripts;
      return allScript.filter((x) => x.id == scriptId)[0].name;
    };

    const getSoftwareName = (softwareId) => {
      let allScript = setting.value.software;
      return allScript.filter((x) => x.id == softwareId)[0].name;
    };

    (async () => {
      try {
        const settingCall = await $store.dispatch("GET_ENVIRONMENT_SETTING");
        Object.assign(setting.value, settingCall);
      } catch (error) {
        console.log(error);
      }
    })();


    // Function to check whether to display the content
    const shouldDisplay = computed(() => {
      if (isAustralia.value && job.value?.pharmacy?.site?.id === 1) {
        return true;  // Show for AU
      } else if (isUS.value && job.value?.pharmacy?.site?.parentId === 1) {
        return true;  // Show for US with specific conditions
      }
      return false;
    });

    const {
      checkFormValidation,
      setFormValidation,
      setFormRefs,
      formsData,
      formsRefs,
    } = formHooks(formsDataArray);

    return {
      travelCompensationCount,
      showShiftInformation,
      isShiftAvailableToBook,
      formatHourlyRate,
      showJobRateSummary,
      showShiftRateBlock,
      formsData,
      handleChange,
      checkFormValidation,
      setFormValidation,
      goBack,
      goNext,
      submit,
      job,
      shifts,
      pageLoading,
      isLoadingShifts,
      checkJobDateStart,
      checkboxValueChange,
      userInfoModalOpen,
      userInfoModal,
      Sum,
      confirmedHours,
      Rate,
      distance,
      share,
      dialogVisible,
      userData,
      groupSubscriptionExist,
      isButtonDisabled,
      applyShift,
      activeNames,
      getScriptName,
      getSoftwareName,
      setting,
      bottomDropUp,
      blockPharmacy,
      formatCurrency,
      superRate,
      bottomDropUpAcceptShift,
      openAcceptShiftDrawer,
      modalVisible: profileRequirements.modalVisible,
      modalMissing: profileRequirements.modalMissing,
      goToRequirement: profileRequirements.goToRequirement,
      SelectAllShifts,
      isAustralia,
      distanceInMiles,
      message,
      isUS,
      buttonLabel,
      calculateTravelCompensation,
      shouldDisplay
    };
  },
};
</script>

<style scoped lang="scss">
:deep(.checkbox_w100),
:deep(.el-checkbox__label),
:deep(.job_shifts_wrapper.full_page) {
  max-width: 100%;
  box-sizing: border-box;
}

.book-shift-card.job_shift_text {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  .book-shift-card__date {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: #1b2a4e;
    padding-right: 36px;
  }

  .info-wrapper {
    display: flex !important;
    flex-direction: column;
    gap: 10px;
    margin-top: 14px;
    width: 100%;
    min-width: 0;
    grid-template-columns: none;
  }

  .day_info_block {
    display: grid !important;
    grid-template-columns: 20px minmax(0, 280px) auto;
    justify-content: start;
    align-items: center;
    column-gap: 8px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    font-size: 15px;
    color: #1b2a4e;

    &:first-child {
      grid-template-columns: 20px minmax(0, 1fr);
    }
  }

  .day_name,
  .day_time {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    color: #1b2a4e;
    min-width: 0;
  }

  .day_name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .day_info_block:not(:first-child) .day_time {
    justify-self: start;
    text-align: left;
    white-space: nowrap;
  }
}
</style>
